import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Card } from '@/shared/components/ui/card'
import {
  getDashboardBarColor,
  resolveDashboardBarWidth,
} from '@/shared/lib/dashboardProgress'
import { buildLeadFilterUrl } from '@/shared/lib/leadNavigation'

const TABS = ['Status', 'Sources', 'Qualification']

export default function LeadsManagementCard({ data, period, customRange }) {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('Status')

  const content = data ?? {}

  const activeData =
    activeTab === 'Status'
      ? content.status
      : activeTab === 'Sources'
        ? content.sources
        : content.qualification

  const items = activeData?.items ?? []

  const total =
    activeData?.total ||
    items.reduce((sum, item) => sum + (Number(item.count) || 0), 0)

  const handleItemClick = (item) => {
    if (activeTab === 'Status') {
      const statusKey = item.name.toLowerCase().replace(/\s+/g, '_')
      navigate(
        buildLeadFilterUrl('/marketing/leads', {
          status: statusKey,
          period,
          customRange,
        }),
      )
    } else if (activeTab === 'Sources') {
      const sourceKey = item.name.toLowerCase()
      navigate(
        buildLeadFilterUrl('/marketing/leads', {
          source: sourceKey,
          period,
          customRange,
        }),
      )
    } else if (activeTab === 'Qualification') {
      const warmthKey = item.name.toLowerCase()
      navigate(
        buildLeadFilterUrl('/marketing/leads', {
          leadWarmth: warmthKey,
          period,
          customRange,
        }),
      )
    }
  }

  return (
    <Card className="h-[320px] overflow-hidden">
      <div className="flex h-full flex-col p-4">
        {/* Static header */}
        <div className="shrink-0">
          <div
            className="cursor-pointer transition hover:opacity-80"
            onClick={() =>
              navigate(
                buildLeadFilterUrl('/marketing/leads', {
                  period,
                  customRange,
                }),
              )
            }
            title="View all leads"
          >
            <h3 className="text-sm font-semibold text-slate-900">
              Leads Management
            </h3>

            <p className="mt-1 text-xs text-slate-400">
              {total} total · {activeData?.label ?? 'live'}
            </p>
          </div>

          {/* Tabs stay fixed */}
          <div className="mt-4 flex flex-wrap gap-1">
            {TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  activeTab === tab
                    ? 'border-slate-900 bg-white text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Only data list scrolls */}
        <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {items.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-400">
              No data yet
            </p>
          ) : (
            <div className="space-y-2">
              {items.map((item) => {
                const color = getDashboardBarColor(item)
                const width = resolveDashboardBarWidth(item, total)

                return (
                  <div
                    key={item.name}
                    onClick={() => handleItemClick(item)}
                    className="group cursor-pointer rounded-xl p-1.5 transition-colors hover:bg-slate-50"
                    title={`Click to view ${item.name} leads`}
                  >
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{
                            backgroundColor: color,
                          }}
                        />

                        <span className="truncate text-[13px] font-medium text-slate-700 group-hover:text-slate-900">
                          {item.name}
                        </span>
                      </div>

                      <div className="flex shrink-0 items-center gap-1">
                        <span className="text-[13px] font-semibold text-slate-900">
                          {item.count}
                        </span>

                        <span className="text-[11px] text-slate-400">
                          leads
                        </span>
                      </div>
                    </div>

                    <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full transition-[width] duration-300"
                        style={{
                          width,
                          backgroundColor: color,
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}
